import React, { memo } from "react";
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { Flag as FlagIcon } from "@mui/icons-material";
import { crmColors } from "../../../theme/colors";

const WA = crmColors.whatsapp;

const STATUS_META = {
  connected: { label: "Connected", color: crmColors.success.main },
  pending: { label: "Pending", color: crmColors.warning.main },
  disabled: { label: "Disabled", color: crmColors.secondary.main },
  token_error: { label: "Token Error", color: crmColors.error.main },
};

const QUALITY_META = {
  GREEN: { label: "Green", color: crmColors.success.main },
  YELLOW: { label: "Yellow", color: crmColors.warning.main },
  RED: { label: "Red", color: crmColors.error.main },
  G: { label: "Green", color: crmColors.success.main },
  Y: { label: "Yellow", color: crmColors.warning.main },
  R: { label: "Red", color: crmColors.error.main },
};

const formatMoney = (n, digits = 2) =>
  `₹${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;

const relativeTime = (value) => {
  if (!value) return "—";
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return "—";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const isServiceActive = (row) => {
  const status = String(row.service_status || "").toLowerCase();
  if (status !== "active") return false;
  if (!row.service_expires_at) return true;
  const exp = new Date(row.service_expires_at).getTime();
  return Number.isNaN(exp) || exp >= Date.now();
};

/**
 * Spec §5.3 — paginated schools table.
 */
const WhatsAppSchoolsTable = ({
  rows = [],
  total = 0,
  page = 0,
  rowsPerPage = 25,
  loading = false,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
}) => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(WA, 0.15)}`, overflow: "hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(WA, 0.06) }}>
              {[
                "School",
                "Connection",
                "Service",
                "Quality",
                "Plan",
                "Usage this cycle",
                "Wallet",
                "Meta cost (est.)",
                "Last message",
                "Flags",
              ].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Box sx={{ py: 4 }}>
                    <LinearProgress sx={{ borderRadius: 1 }} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No schools match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const status = STATUS_META[row.connection_status] || {
                  label: row.connection_status || "—",
                  color: crmColors.secondary.main,
                };
                const qKey = String(row.quality_rating || "").toUpperCase();
                const quality = QUALITY_META[qKey] || null;
                const included = Number(row.included_messages) || 0;
                const used = Number(row.messages_used_this_cycle) || 0;
                const wallet = Number(row.wallet_balance) || 0;
                const threshold = Number(row.low_balance_threshold) || 50;
                const lowWallet = wallet <= threshold;
                const serviceActive = isServiceActive(row);
                const serviceExpired =
                  String(row.service_status || "").toLowerCase() === "active" &&
                  row.service_expires_at &&
                  !serviceActive;

                return (
                  <TableRow
                    key={row.school_code || row.id}
                    hover
                    onClick={() => onRowClick?.(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {row.school_name || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {row.school_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={status.label}
                        sx={{ fontWeight: 700, bgcolor: alpha(status.color, 0.14), color: status.color }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      <Chip
                        size="small"
                        label={serviceExpired ? "Expired" : serviceActive ? "Active" : "InActive"}
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(
                            serviceExpired
                              ? crmColors.error.main
                              : serviceActive
                                ? crmColors.success.main
                                : crmColors.secondary.main,
                            0.14
                          ),
                          color: serviceExpired
                            ? crmColors.error.main
                            : serviceActive
                              ? crmColors.success.main
                              : crmColors.secondary.main,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                        {row.service_activated_at
                          ? `Since ${relativeTime(row.service_activated_at)}`
                          : "No service row"}
                      </Typography>
                      {row.service_expires_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          Exp {formatDateTime(row.service_expires_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {quality ? (
                        <Chip
                          size="small"
                          label={quality.label}
                          sx={{ fontWeight: 700, bgcolor: alpha(quality.color, 0.14), color: quality.color }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {row.plan_name || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Usage
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      {included > 0 ? (
                        <Box>
                          <Typography variant="caption" fontWeight={700}>
                            {used.toLocaleString()} / {included.toLocaleString()}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, (used / included) * 100)}
                            color={used >= included ? "warning" : "success"}
                            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2">{used.toLocaleString() || "—"}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color={lowWallet ? "error.main" : "inherit"}>
                        {formatMoney(wallet, 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatMoney(row.meta_cost_this_month, 2)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{relativeTime(row.last_message_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.is_flagged ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <FlagIcon sx={{ fontSize: 18, color: crmColors.error.main }} />
                        </Stack>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
};

export default memo(WhatsAppSchoolsTable);
